/**
 * Client-Side Image Compressor & Optimizer
 * Compresses receipt & payment proof images (InstaPay / Mobile Wallets)
 * to ensure crisp readability while keeping payload size small (~30KB - 80KB)
 * so it effortlessly stores in Firestore documents without exceeding the 1MB limit.
 */

export interface CompressedImageResult {
  file: File;
  dataUrl: string;
  width: number;
  height: number;
  sizeBytes: number;
}

/**
 * Resizes and compresses an image file into an optimized JPEG.
 * @param file The original user-selected file
 * @param maxDimension Maximum width or height (default 1000px, optimal for receipts)
 * @param quality JPEG compression quality between 0.1 and 1.0 (default 0.75)
 */
export async function compressPaymentProof(
  file: File,
  maxDimension = 1000,
  quality = 0.75
): Promise<CompressedImageResult> {
  return new Promise((resolve, reject) => {
    // 1. Validate input file
    if (!file || !file.type.startsWith('image/')) {
      return reject(new Error('الملف المرفق ليس صورة صالحة'));
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('تعذر قراءة ملف الصورة'));

    reader.onload = (readerEvent) => {
      const img = new Image();
      img.onerror = () => reject(new Error('تعذر تحميل الصورة للمعالجة'));

      img.onload = () => {
        try {
          let { width, height } = img;

          // 2. Scale down if larger than maxDimension
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }

          // 3. Draw on canvas
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            return reject(new Error('تعذر إنشاء سياق رسم للصورة'));
          }

          // Fill white background (useful for transparent PNG screenshots)
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);

          // Smooth rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);

          // 4. Export as JPEG Data URL
          let dataUrl = canvas.toDataURL('image/jpeg', quality);

          // If still over 180KB, recompress with lower quality
          if (dataUrl.length > 240000 && quality > 0.5) {
            dataUrl = canvas.toDataURL('image/jpeg', 0.55);
          }

          // 5. Convert to Blob & File
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                return reject(new Error('تعذر استخراج ملف الصورة المضغوط'));
              }

              const cleanName = file.name.replace(/\.[^/.]+$/, '') + '.jpg';
              const compressedFile = new File([blob], cleanName, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });

              resolve({
                file: compressedFile,
                dataUrl,
                width,
                height,
                sizeBytes: blob.size,
              });
            },
            'image/jpeg',
            quality
          );
        } catch (err) {
          reject(err);
        }
      };

      img.src = readerEvent.target?.result as string;
    };

    reader.readAsDataURL(file);
  });
}
